import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { Customer } from "@/types/customer-type";
import {
  FulfillmentCarrier,
  FulfillmentMethod,
} from "@/types/fulfillment-type";
import {
  OrderCreateFormValues,
  orderCreateSchema,
  OrderStatus,
} from "@/types/order-type";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";

import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Package
} from "lucide-react";

import { DEFAULT_STORE_ID } from "@/constants/product-attributes";
import { calculateOrderTotals, cn } from "@/lib/utils";
import { useProductVariants } from "@/services/product-variant-service";

interface FloatingOrderPanelProps {
  open: boolean;
  currentCustomer?: Customer;
  initialData?: Partial<OrderCreateFormValues>;
  onCancel: () => void;
  onConfirm: (values: OrderCreateFormValues) => Promise<void> | void;
}

/**
 * FloatingOrderPanel
 * - Items come from drag & drop (no product list / OrderItemsEditor)
 * - Allows dropping more products to add / increment qty
 * - Can be minimized or expanded
 * - Buttons: Cancel / Confirm order
 */
export function FloatingOrderPanel({
  open,
  currentCustomer,
  initialData,
  onCancel,
  onConfirm,
}: FloatingOrderPanelProps) {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { data: variantsData } = useProductVariants();
  const allVariants = variantsData || [];

  const form = useForm<OrderCreateFormValues>({
    resolver: zodResolver(orderCreateSchema),
    mode: "onChange",
    defaultValues: {
      customerId: currentCustomer?.id,
      status: OrderStatus.DRAFT,
      notes: "",
      items: [],
      fulfillment: {
        method: FulfillmentMethod.DELIVERY_CITY,
        carrier: FulfillmentCarrier.VIREAK_BUNTHAM,
        price: "2",
      },
      ...initialData,
    },
  });

  // keep form in sync when dialog (panel) reopens or customer changes
  const prevOpenRef = useRef<boolean>(false);
  useEffect(() => {
    if (!open || prevOpenRef.current) return;
    form.reset({
      customerId: currentCustomer?.id,
      status: OrderStatus.DRAFT,
      notes: "",
      items: [],
      fulfillment: {
        method: FulfillmentMethod.DELIVERY_CITY,
        carrier: FulfillmentCarrier.VIREAK_BUNTHAM,
        price: "2",
      },
      ...initialData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    prevOpenRef.current = open;
  }, [open, currentCustomer?.id]);

  const watchFulfillmentPrice = form.watch("fulfillment.price");
  const watchItems = form.watch("items");

  const calculateTotal = () => {
    const fulfillmentPrice = parseFloat(watchFulfillmentPrice || "0");
    const items = watchItems || [];

    const itemsForCalc = items.map((i) => {
      const v = allVariants.find((v) => String(v.id) === String(i.variantId));
      const unitPrice = v ? Number(v.price || 0) : 0;
      return { unitPrice, quantity: i.quantity };
    });

    const totals = calculateOrderTotals({
      items: itemsForCalc,
      discountPercentage: 0,
      deliveryFee: fulfillmentPrice,
    });
    // Total is calculated but not stored in form since it's not in DTO
  };

  useEffect(() => {
    calculateTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchFulfillmentPrice, allVariants, watchItems]);

  const handleConfirm = async (e: any) => {
    await onConfirm(form.getValues());
  };

  // 🔥 Drag & Drop support: add / increment items
  const handleDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault(); // allow drop
    setIsDragOver(true);
  };

  const handleDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
    // only reset if actually leaving the card, not moving inside
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const variantId =
      e.dataTransfer.getData("application/variant-id") ||
      e.dataTransfer.getData("application/product-id"); // fallback

    if (!variantId) return;

    const currentItems = form.getValues("items") || [];
    const index = currentItems.findIndex(
      (it) => String(it.variantId) === String(variantId)
    );

    let newItems;

    if (index >= 0) {
      // same product → +1 quantity
      newItems = currentItems.map((it, idx) =>
        idx === index ? { ...it, quantity: (it.quantity || 0) + 1 } : it
      );
    } else {
      // new product line
      newItems = [
        ...currentItems,
        { variantId, quantity: 1 }, // adapt extra fields (name, note, etc.) as needed
      ];
    }

    form.setValue("items", newItems, { shouldDirty: true, shouldTouch: true });
    calculateTotal();
  };

  if (!open || !currentCustomer) return null;

  const itemCount =
    (form.getValues("items") || []).reduce(
      (sum, it) => sum + (it.quantity || 0),
      0
    ) || 0;
  const rawItems = watchItems || [];

  const detailedItems = rawItems.map((it) => {
    const variant = allVariants.find(
      (v) => String(v.id) === String(it.variantId)
    );

    const unitPrice = variant ? Number(variant.price || 0) : 0;
    const qty = it.quantity || 0;

    return {
      key: `${it.variantId}`,
      variantId: it.variantId,
      qty,
      unitPrice,
      lineTotal: unitPrice * qty,
      // try to compose nice labels from what you have on variant
      productName: variant?.productId || variant?.name || "Unknown product",
      variantLabel: variant?.name || variant?.sku || null,
      imageUrl: variant?.imageUrl || null,
      sku: variant?.sku || null,
    };
  });

  return (
    <div
      className={cn(
        "fixed right-4 bottom-4 z-50 transition-all duration-200",
        open
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-4 pointer-events-none"
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Card
        className={cn(
          "w-[380px] max-h-[80vh] shadow-lg border bg-white flex flex-col transition-all duration-150",
          isDragOver && "ring-2 ring-blue-500 ring-offset-2"
        )}
      >
        {/* Header */}
        <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2">
          <div className="flex-1 min-w-0 flex items-start gap-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
              {currentCustomer.profileImageUrl ? (
                <img
                  src={currentCustomer.profileImageUrl}
                  alt={currentCustomer.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <span>{currentCustomer.name?.charAt(0) || "C"}</span>
              )}
            </div>
            <div className="min-w-0">
              <CardTitle className="text-sm font-semibold truncate">
                New Order – {currentCustomer.name}
              </CardTitle>
              <p className="text-[11px] text-slate-500 flex items-center gap-1 truncate">
                <Calendar className="w-3 h-3" />
                Customer since{" "}
                {typeof currentCustomer.createdAt === "string"
                  ? new Date(currentCustomer.createdAt).toLocaleDateString(
                    "en-US",
                    { month: "short", year: "numeric" }
                  )
                  : "Unknown"}
              </p>
            </div>
          </div>

          {/* Expand / collapse */}
          <button
            type="button"
            onClick={() => setIsExpanded((v) => !v)}
            className="ml-2 text-slate-400 hover:text-slate-600 flex items-center justify-center rounded-full border border-slate-200 w-7 h-7"
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronUp className="h-3.5 w-3.5" />
            )}
          </button>
        </CardHeader>

        {/* Content */}
        <CardContent className="pt-0 flex-1 flex flex-col overflow-hidden">
          <Form {...form}>
            <div className="space-y-3 text-xs text-slate-600">
              {/* Compact summary always visible */}
              <div
                className={cn(
                  "rounded-md px-3 py-2 flex flex-col gap-1 border",
                  isDragOver
                    ? "border-blue-400 bg-blue-50/60"
                    : "border-slate-200 bg-slate-50"
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-50">
                      <Package className="h-3.5 w-3.5 text-emerald-600" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-slate-800 truncate">
                        {itemCount} item{itemCount !== 1 ? "s" : ""} in order
                      </p>
                      <p className="text-[11px] text-slate-500 truncate">
                        {isDragOver
                          ? "Drop to add product to this order"
                          : "Drag products here to add or increase quantity"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded: contact + address + shipping/payment + totals */}
              {isExpanded && (
                <div className="space-y-3 overflow-y-auto max-h-[42vh] pr-1">
                  {/* Contact & address (as before) */}
                  <div className="grid grid-cols-1 gap-3">
                    {/* ... Phone + Address blocks ... */}
                  </div>

                  {/* 🔥 Order items detail list */}
                  <div className="rounded-md border border-slate-200 bg-white">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                      <p className="text-xs font-semibold text-slate-800">
                        Order items
                      </p>
                      <p className="text-[11px] text-slate-500">
                        Drag products again to increase quantity
                      </p>
                    </div>

                    {detailedItems.length === 0 ? (
                      <div className="px-3 py-3 text-[11px] text-slate-500">
                        No items yet. Drag a product card onto this customer to
                        add.
                      </div>
                    ) : (
                      <ul className="divide-y divide-slate-100 max-h-40 overflow-y-auto">
                        {detailedItems.map((item) => (
                          <li
                            key={item.key}
                            className="px-3 py-2 flex items-center gap-3"
                          >
                            {/* Thumbnail */}
                            <div className="h-10 w-10 rounded-md border border-slate-200 bg-slate-50 overflow-hidden flex-shrink-0">
                              {item.imageUrl ? (
                                <img
                                  src={item.imageUrl}
                                  alt={item.productName}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="h-full w-full grid place-items-center text-[10px] text-slate-400">
                                  No image
                                </div>
                              )}
                            </div>

                            {/* Name + variant */}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-slate-800 truncate">
                                {item.productName}
                              </p>
                              {item.variantLabel && (
                                <p className="text-[11px] text-slate-500 truncate">
                                  {item.variantLabel}
                                </p>
                              )}
                              {item.sku && (
                                <p className="text-[10px] text-slate-400 truncate">
                                  SKU: {item.sku}
                                </p>
                              )}
                            </div>

                            {/* Price x qty */}
                            <div className="flex flex-col items-end gap-0.5 text-right">
                              <span className="text-[11px] text-slate-500">
                                ${item.unitPrice.toFixed(2)} × {item.qty}
                              </span>
                              <span className="text-xs font-semibold text-slate-900">
                                ${item.lineTotal.toFixed(2)}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Order totals */}
                  {/* <OrderTotalCard control={form.control} /> */}
                </div>
              )}
            </div>
          </Form>
        </CardContent>

        {/* Footer: Cancel / Confirm */}
        <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="flex-1"
            onClick={handleConfirm}
            disabled={itemCount === 0 || isSubmitting}
          >
            Confirm order
          </Button>
        </div>
      </Card>
    </div>
  );
}
